/**
 * Brand completeness — share of identity sections the client has filled in.
 * Nudges clients to feed the capsule. Deterministic, one section = one
 * point, 10 sections total.
 */
export const IDENTITY_SECTION_COUNT = 10;

export function identitySectionsFilled(identity) {
  const tone = identity.tone ?? {};
  const toneTouched =
    tone.formalCasual !== 50 || tone.playfulSerious !== 50 || tone.boldReserved !== 50;

  const fonts = identity.fonts ?? {};

  const checks = [
    Boolean(identity.industry?.trim().length),
    toneTouched,
    (identity.voiceExamples?.length ?? 0) > 0,
    (identity.usps?.length ?? 0) > 0,
    (identity.bannedWords?.length ?? 0) > 0,
    Boolean(identity.complianceNotes?.trim().length),
    (identity.palette?.length ?? 0) > 0,
    Boolean(fonts.heading?.trim().length) || Boolean(fonts.body?.trim().length),
    (identity.audiences?.length ?? 0) > 0,
    (identity.competitors?.length ?? 0) > 0,
  ];

  return checks.filter(Boolean).length;
}

/** 0–100, rounded. */
export function completenessPercent(identity) {
  return Math.round((identitySectionsFilled(identity) / IDENTITY_SECTION_COUNT) * 100);
}

/**
 * Required-to-save fields — a stricter, narrower gate than overall
 * completeness. A brand can be saved once these four are filled in, even
 * if voice examples/USPs/banned words/compliance notes/audiences/
 * competitors are still empty. This is deliberately separate from
 * completenessPercent(), which still scores all 10 sections for the
 * progress bar.
 */
export const REQUIRED_IDENTITY_FIELDS = ["industry", "language", "palette"];

/** Which of the required fields are still missing, as human labels. */
export function missingRequiredFields(identity) {
  const missing = [];

  if (!identity.industry?.trim().length) missing.push("Industry");
  if (!identity.language?.trim().length) missing.push("Language");
  if (!(identity.palette?.length > 0)) missing.push("Palette");

  const fonts = identity.fonts ?? {};
  if (!fonts.heading?.trim().length || !fonts.body?.trim().length) missing.push("Fonts");

  return missing;
}

export function identityIsReady(identity) {
  return missingRequiredFields(identity).length === 0;
}